import { treasuryAmount, sumTreasuryAmounts, subtractTreasuryAmounts } from '@liquid-spirit/api/treasury-figures';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { readBudgetDetailResponse } from '@liquid-spirit/api/domains/budgets';
import { credentialScope } from '../utils/credentialScope';
import { useCommunityContext } from './CommunityContext';
import { useAuthContext } from './AuthContext';
import {
  approveBudgetAllocationRequest as apiApproveBudgetAllocationRequest,
  approveBudget as apiApproveBudget,
  createBudget as apiCreateBudget,
  createBudgetAllocation as apiCreateBudgetAllocation,
  deleteBudgetAllocation as apiDeleteBudgetAllocation,
  declineBudgetAllocationRequest as apiDeclineBudgetAllocationRequest,
  getCurrentBudget,
  requestBudgetAllocation as apiRequestBudgetAllocation,
  updateBudget as apiUpdateBudget,
  updateBudgetAllocation as apiUpdateBudgetAllocation,
} from '../services/BudgetService';

const EMPTY_BUDGET = {
  id: '',
  name: 'Community Budget',
  periodLabel: '',
  startDate: null,
  endDate: null,
  status: 'draft',
  currency: 'AUD',
};
const EMPTY_ALLOCATIONS = [];
const EMPTY_REQUESTS = [];

const BudgetContext = createContext(null);

const normalizePerson = (value) => {
  if (!value) return null;

  const fullName = typeof value?.fullName === 'string'
    ? value.fullName.trim()
    : [value?.firstName, value?.lastName].filter(Boolean).join(' ').trim();

  return {
    id: String(value?._id || value?.id || ''),
    fullName: fullName || value?.email || 'Unknown user',
    email: value?.email || '',
  };
};

const normalizeBudgetEntries = (entries = []) => (
  entries
    .map((item, index) => ({
      id: String(item?._id || item?.id || `allocation-${index}`),
      categoryKey: item?.categoryKey || 'other',
      title: item?.title || item?.itemName || item?.name || 'Untitled allocation',
      description: item?.description || '',
      allocatedAmount: treasuryAmount(item?.allocatedAmount ?? item?.budgetedAmount ?? item?.amount),
      actualAmount: treasuryAmount(item?.actualAmount ?? item?.spentAmount),
      committedAmount: treasuryAmount(item?.committedAmount),
      advanceOutstandingAmount: treasuryAmount(item?.advanceOutstandingAmount),
      uncommittedAmount: treasuryAmount(item?.uncommittedAmount),
      status: item?.status || 'approved',
      fundId: item?.fundId || item?.fund?._id || item?.fund?.id || '',
      fundName: item?.fundName || item?.fund?.fundName || item?.fund?.name || '',
      notes: item?.notes || '',
      decisionNotes: item?.decisionNotes || '',
      createdBy: normalizePerson(item?.createdBy),
      updatedBy: normalizePerson(item?.updatedBy),
      reviewedBy: normalizePerson(item?.reviewedBy),
      reviewedAt: item?.reviewedAt || null,
      createdAt: item?.createdAt || null,
      updatedAt: item?.updatedAt || null,
      sortOrder: Number.isFinite(Number(item?.sortOrder)) ? Number(item.sortOrder) : index,
      isFallback: item?.isFallback === true,
    }))
    .sort((left, right) => left.sortOrder - right.sortOrder)
);

const toBudgetSnapshot = (
  budget = EMPTY_BUDGET,
  allocations = EMPTY_ALLOCATIONS,
  requests = EMPTY_REQUESTS,
) => {
  const normalizedAllocations = normalizeBudgetEntries(allocations);
  const normalizedRequests = normalizeBudgetEntries(requests);

  const totalAllocated = sumTreasuryAmounts(normalizedAllocations.map((item) => item.allocatedAmount));
  const totalActual = sumTreasuryAmounts(normalizedAllocations.map((item) => item.actualAmount));
  const totalAdvanceOutstanding = sumTreasuryAmounts(normalizedAllocations.map((item) => item.advanceOutstandingAmount));
  const totalCommitted = sumTreasuryAmounts(normalizedAllocations.map((item) => item.committedAmount));

  return {
    id: String(budget?._id || budget?.id || EMPTY_BUDGET.id),
    name: budget?.name || EMPTY_BUDGET.name,
    periodLabel: budget?.periodLabel || budget?.label || EMPTY_BUDGET.periodLabel,
    startDate: budget?.startDate || EMPTY_BUDGET.startDate,
    endDate: budget?.endDate || EMPTY_BUDGET.endDate,
    status: budget?.status || EMPTY_BUDGET.status,
    currency: budget?.currency || EMPTY_BUDGET.currency,
    notes: budget?.notes || '',
    approvedAt: budget?.approvedAt || null,
    updatedAt: budget?.updatedAt || null,
    approvedBy: budget?.approvedBy || null,
    totals: {
      allocatedAmount: totalAllocated,
      actualAmount: totalActual,
      committedAmount: totalCommitted,
      advanceOutstandingAmount: totalAdvanceOutstanding,
      uncommittedAmount: subtractTreasuryAmounts(subtractTreasuryAmounts(subtractTreasuryAmounts(totalAllocated, totalActual), totalCommitted), totalAdvanceOutstanding),
      remainingAmount: subtractTreasuryAmounts(totalAllocated, totalActual),
    },
    allocations: normalizedAllocations,
    requests: normalizedRequests,
  };
};

const normalizeBudgetPayload = payload => {
  readBudgetDetailResponse(payload);
  const {budget, allocations, requests} = payload.data;
  return toBudgetSnapshot(budget, allocations, requests);
};

export const BudgetProvider = ({ children }) => {
  const {token} = useAuthContext();
  const {community} = useCommunityContext();
  const communityId = community?._id || community?.id || '';
  return <ScopedBudgetProvider key={`${credentialScope(token)}:${communityId}`} token={token} communityId={communityId}>{children}</ScopedBudgetProvider>;
};

const ScopedBudgetProvider = ({children, token, communityId}) => {
  const [budget, setBudget] = useState(() => toBudgetSnapshot());
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isFallbackData, setIsFallbackData] = useState(true);
  const alive = useRef(false), version = useRef(0), busy = useRef(false);
  const options = useMemo(() => ({token, ...(communityId ? {params: {communityId}} : {})}), [token, communityId]);
  const clearMessages = useCallback(() => {setError(''); setSuccess('');}, []);
  const adopt = useCallback(response => {
    const snapshot = normalizeBudgetPayload(response);
    setBudget(snapshot); setIsFallbackData(false);
    return snapshot;
  }, []);
  const loadBudget = useCallback(async ({silent = false} = {}) => {
    if (!token || !alive.current) return {success: false, error: 'Missing authentication token'};
    const request = ++version.current;
    if (!silent) {setLoading(true); setError('');}
    try {
      const response = await getCurrentBudget(options);
      if (!alive.current || request !== version.current) return {success: false};
      return {success: true, data: adopt(response)};
    } catch (failure) {
      if (alive.current && request === version.current) {
        setBudget(toBudgetSnapshot()); setIsFallbackData(true);
        setError('Could not load the community budget. Please try again.');
      }
      return {success: false, error: failure.message, code: failure.code, status: failure.status, apiCode: failure.apiCode};
    } finally {if (alive.current && request === version.current) setLoading(false);}
  }, [token, options, adopt]);
  const mutate = useCallback(async (action, args, message) => {
    if (!token || !alive.current) return {success: false, message: 'Missing authentication token.'};
    if (busy.current) return {success: false, message: 'A budget change is already in progress.'};
    busy.current = true; version.current += 1;
    setSubmitting(true); setLoading(false); clearMessages();
    try {
      const response = await action({...options, ...args});
      if (!alive.current) return {success: false};
      version.current += 1; // A read begun during the write cannot overwrite its reply.
      adopt(response); setSuccess(message);
      return {success: true};
    } catch (failure) {
      const message = failure.code === 'INVALID_API_RESPONSE' || !failure.status && failure.code !== 'INVALID_API_REQUEST'
        ? 'The change could not be confirmed. Refresh the budget before trying it again.'
        : failure.message || 'Unable to change the budget.';
      if (alive.current) setError(message);
      return {success: false, message, code: failure.code, status: failure.status, apiCode: failure.apiCode};
    } finally {busy.current = false; if (alive.current) setSubmitting(false);}
  }, [token, options, clearMessages, adopt]);
  useEffect(() => {
    alive.current = true; void loadBudget();
    return () => {alive.current = false; version.current += 1;};
  }, [loadBudget]);
  const actions = useMemo(() => ({
    createBudget: payload => mutate(apiCreateBudget, {payload}, 'Budget created successfully.'),
    updateBudget: ({budgetId, ...payload}) => mutate(apiUpdateBudget, {budgetId, payload}, 'Budget updated successfully.'),
    approveBudget: ({budgetId, notes}) => mutate(apiApproveBudget, {budgetId, payload: {notes}}, 'Budget approved successfully.'),
    createAllocation: ({budgetId, ...payload}) => mutate(apiCreateBudgetAllocation, {budgetId, payload}, 'Budget allocation created successfully.'),
    requestAllocation: ({budgetId, ...payload}) => mutate(apiRequestBudgetAllocation, {budgetId, payload}, 'Budget allocation request submitted successfully.'),
    updateAllocation: ({allocationId, ...payload}) => mutate(apiUpdateBudgetAllocation, {allocationId, payload}, 'Budget allocation updated successfully.'),
    deleteAllocation: ({allocationId}) => mutate(apiDeleteBudgetAllocation, {allocationId}, 'Budget allocation removed successfully.'),
    approveAllocationRequest: ({allocationId, ...payload}) => mutate(apiApproveBudgetAllocationRequest, {allocationId, payload}, 'Budget allocation request approved successfully.'),
    declineAllocationRequest: ({allocationId, ...payload}) => mutate(apiDeclineBudgetAllocationRequest, {allocationId, payload}, 'Budget allocation request declined successfully.'),
  }), [mutate]);
  const value = useMemo(() => ({budget, allocations: budget.allocations, requests: budget.requests, loading, submitting, error, success, isFallbackData, clearMessages, loadBudget, ...actions}), [budget, loading, submitting, error, success, isFallbackData, clearMessages, loadBudget, actions]);
  return <BudgetContext.Provider value={value}>{children}</BudgetContext.Provider>;
};

export const useBudgetContext = () => {
  const ctx = useContext(BudgetContext);
  if (!ctx) {
    throw new Error('useBudgetContext must be used within a BudgetProvider');
  }
  return ctx;
};
